import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
    Paper,
    Text,
    Button,
    Group,
    Stack,
    Transition,
    Box
} from '@mantine/core'
import { IconShieldCheck } from '@tabler/icons-react'

const COOKIE_CONSENT_KEY = 'privacy_consent_given'

export const CookieWall: React.FC = () => {
    const { t } = useTranslation()
    const [isVisible, setIsVisible] = useState(false)
    const [hasConsented, setHasConsented] = useState(false)

    useEffect(() => {
        // Check if user has already given consent
        const consent = localStorage.getItem(COOKIE_CONSENT_KEY)
        if (!consent) {
            // Show the cookie wall after a short delay to ensure page is loaded
            const timer = setTimeout(() => {
                setIsVisible(true)
            }, 1500) // Increased delay to 1.5 seconds
            return () => clearTimeout(timer)
        } else {
            setHasConsented(true)
        }
    }, [])

    const handleAccept = () => {
        localStorage.setItem(COOKIE_CONSENT_KEY, 'true')
        setHasConsented(true)
        setIsVisible(false)
    }

    const handleDecline = () => {
        // Clear all localStorage data except essential ones
        const essentialKeys = ['privacy_consent_given']
        const keysToRemove: string[] = []

        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i)
            if (key && !essentialKeys.includes(key)) {
                keysToRemove.push(key)
            }
        }

        keysToRemove.forEach(key => localStorage.removeItem(key))

        localStorage.setItem(COOKIE_CONSENT_KEY, 'declined')
        setHasConsented(true)
        setIsVisible(false)
    }

    if (hasConsented) {
        return null
    }

    return (
        <Transition
            mounted={isVisible}
            transition="slide-up"
            duration={400}
            timingFunction="ease-out"
        >
            {(styles) => (
                <Box
                    style={{
                        ...styles,
                        position: 'fixed',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        zIndex: 1000,
                        padding: '16px',
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                    }}
                    role="dialog"
                    aria-labelledby="cookie-wall-title"
                    aria-describedby="cookie-wall-description"
                >
                    <Paper
                        shadow="lg"
                        p="md"
                        radius="md"
                        style={{
                            maxWidth: '600px',
                            margin: '0 auto',
                            backgroundColor: 'var(--mantine-color-body)',
                        }}
                    >
                        <Stack gap="sm">
                            <Group gap="xs">
                                <IconShieldCheck size={20} color="var(--mantine-color-blue-6)" />
                                <Text size="lg" fw={600} id="cookie-wall-title">
                                    {t('cookieWall.title')}
                                </Text>
                            </Group>

                            <Text size="sm" c="dimmed" id="cookie-wall-description">
                                {t('cookieWall.description')}
                            </Text>

                            <Text size="xs" c="dimmed">
                                {t('cookieWall.details')}
                            </Text>

                            <Group justify="flex-end" gap="sm">
                                <Button
                                    variant="subtle"
                                    size="sm"
                                    onClick={handleDecline}
                                >
                                    {t('cookieWall.decline')}
                                </Button>
                                <Button
                                    size="sm"
                                    onClick={handleAccept}
                                >
                                    {t('cookieWall.accept')}
                                </Button>
                            </Group>
                        </Stack>
                    </Paper>
                </Box>
            )}
        </Transition>
    )
} 